import React, { useState } from 'react';
import { Tabs, Card, Form, Input, Button, message, Upload, Switch, Select } from 'antd';
import { BgColorsOutlined, MailOutlined, SafetyCertificateOutlined, UploadOutlined } from '@ant-design/icons';
import './PlatformSettings.css';

const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

function PlatformSettings() {
  const [activeTab, setActiveTab] = useState('branding');
  const [brandingForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [rolesForm] = Form.useForm();

  const handleBrandingSubmit = async (values) => {
    try {
      console.log('Branding Settings:', values);
      message.success('Branding settings saved successfully');
    } catch (error) {
      message.error('Failed to save branding settings');
    }
  };

  const handleEmailSubmit = async (values) => {
    try {
      console.log('Email Settings:', values);
      message.success('Email template settings saved successfully');
    } catch (error) {
      message.error('Failed to save email settings');
    }
  };

  const handleRolesSubmit = async (values) => {
    try {
      console.log('Roles Settings:', values);
      message.success('Role & permission settings saved successfully');
    } catch (error) {
      message.error('Failed to save role settings');
    }
  };

  return (
    <div className="platform-settings-container">
      <div className="page-header">
        <h1>Platform Settings</h1>
        <p>Configure platform branding, email templates, and default permissions</p>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} className="platform-settings-tabs">
        <TabPane
          tab={
            <span>
              <BgColorsOutlined />
              Branding & White Label
            </span>
          }
          key="branding"
        >
          <Card title="Branding & White Label Configuration">
            <Form
              form={brandingForm}
              layout="vertical"
              onFinish={handleBrandingSubmit}
              initialValues={{
                platform_name: 'Pathmarkr',
                primary_color: '#1890ff',
                secondary_color: '#52c41a',
                enable_whitelabel: false,
              }}
            >
              <Form.Item
                name="platform_name"
                label="Platform Name"
                rules={[{ required: true }]}
              >
                <Input placeholder="Pathmarkr" />
              </Form.Item>

              <Form.Item
                name="logo"
                label="Platform Logo"
              >
                <Upload>
                  <Button icon={<UploadOutlined />}>Upload Logo</Button>
                </Upload>
              </Form.Item>

              <Form.Item
                name="favicon"
                label="Favicon"
              >
                <Upload>
                  <Button icon={<UploadOutlined />}>Upload Favicon</Button>
                </Upload>
              </Form.Item>

              <Form.Item
                name="primary_color"
                label="Primary Color"
              >
                <Input type="color" />
              </Form.Item>

              <Form.Item
                name="secondary_color"
                label="Secondary Color"
              >
                <Input type="color" />
              </Form.Item>

              <Form.Item
                name="enable_whitelabel"
                label="Enable White Label Mode"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Save Branding Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <MailOutlined />
              Email & Notification Templates
            </span>
          }
          key="email"
        >
          <Card title="Email & Notification Templates">
            <Form
              form={emailForm}
              layout="vertical"
              onFinish={handleEmailSubmit}
              initialValues={{
                template_type: 'welcome',
                subject: '',
                body: '',
              }}
            >
              <Form.Item
                name="template_type"
                label="Template Type"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="welcome">Welcome Email</Option>
                  <Option value="password_reset">Password Reset</Option>
                  <Option value="campaign_approved">Campaign Approved</Option>
                  <Option value="campaign_rejected">Campaign Rejected</Option>
                  <Option value="invoice">Invoice</Option>
                  <Option value="subscription">Subscription Update</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="subject"
                label="Email Subject"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter email subject" />
              </Form.Item>

              <Form.Item
                name="body"
                label="Email Body"
                rules={[{ required: true }]}
              >
                <TextArea rows={10} placeholder="Enter email body (supports HTML)" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Save Email Template
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SafetyCertificateOutlined />
              Default Roles & Permissions
            </span>
          }
          key="roles"
        >
          <Card title="Default Roles & Permissions Configuration">
            <Form
              form={rolesForm}
              layout="vertical"
              onFinish={handleRolesSubmit}
              initialValues={{
                default_client_role: 'client',
                default_agency_role: 'agency',
                allow_client_create_campaigns: true,
                allow_agency_manage_clients: true,
              }}
            >
              <Form.Item
                name="default_client_role"
                label="Default Client Role"
              >
                <Select>
                  <Option value="client">Client</Option>
                  <Option value="client_readonly">Client (Read Only)</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="default_agency_role"
                label="Default Agency Role"
              >
                <Select>
                  <Option value="agency">Agency</Option>
                  <Option value="agency_admin">Agency Admin</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="allow_client_create_campaigns"
                label="Allow Clients to Create Campaigns"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="allow_agency_manage_clients"
                label="Allow Agencies to Manage Clients"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="allow_client_view_analytics"
                label="Allow Clients to View Analytics"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Save Role Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
}

export default PlatformSettings;
